import { compactBulkAnswerFields } from './compactBulkAnswer';
import { formatStructuredAnswer } from './formatStructuredAnswer';

/** Matches QuestionBankDto / bulk upload API — omit id (assigned by DB). */

export const VALID_SUB_CATEGORY1 = ['basics', 'intermediate', 'advanced'];

export const BULK_QUESTION_EXAMPLE = [
  {
    question:
      'Design a scalable notification system for email, push, SMS, and in-app delivery at scale.',
    answer:
      'High level: API gateway accepts events, notification service routes by channel, workers deliver async. Mention queues for scale and idempotent handlers.',
    extraInfo:
      '1. Core components:\n- Notification API gateway, orchestrator, template service, per-channel workers\n\n2. Data models:\n- User prefs, templates, delivery log\n\n3. Deep dives:\n- DLQ, rate limits, fatigue caps, multi-region.',
    category: 'System Design',
    topic: 'Notification System',
    subCategory1: 'advanced',
    subCategory2: 'architecture',
  },
];

export const BULK_JSON_PLACEHOLDER = JSON.stringify(BULK_QUESTION_EXAMPLE, null, 2);

function cleanString(value) {
  if (value == null) return '';
  return String(value).trim();
}

/**
 * One row in the bulk upload array, with form defaults applied to missing fields.
 */
export function normalizeBulkQuestionItem(raw, defaults = {}) {
  const item = raw && typeof raw === 'object' ? raw : {};
  const merged = {
    question: item.question ?? defaults.question,
    answer: item.answer ?? defaults.answer,
    extraInfo: item.extraInfo ?? defaults.extraInfo,
    category: item.category ?? defaults.category,
    topic: item.topic ?? defaults.topic,
    subCategory1: item.subCategory1 ?? defaults.subCategory1,
    subCategory2: item.subCategory2 ?? defaults.subCategory2,
  };

  const subCategory1 = cleanString(merged.subCategory1).toLowerCase();

  const formattedAnswer = formatStructuredAnswer(cleanString(merged.answer));
  const formattedExtra = formatStructuredAnswer(cleanString(merged.extraInfo));
  const { answer, extraInfo } = compactBulkAnswerFields(formattedAnswer, formattedExtra);

  return {
    question: cleanString(merged.question),
    answer,
    extraInfo,
    category: cleanString(merged.category),
    topic: cleanString(merged.topic),
    subCategory1,
    subCategory2: cleanString(merged.subCategory2) || 'general',
  };
}

export function normalizeBulkQuestions(input, defaults = {}) {
  let arr = input;
  if (!Array.isArray(arr)) {
    if (arr && typeof arr === 'object' && arr.question) {
      arr = [arr];
    } else {
      return { items: [], errors: ['JSON must be an array of question objects.'] };
    }
  }

  const errors = [];
  const items = arr.map((raw, index) => {
    const row = normalizeBulkQuestionItem(raw, defaults);
    const label = `Item ${index + 1}`;

    if (!row.question) errors.push(`${label}: "question" is required.`);
    if (!row.answer) errors.push(`${label}: "answer" is required.`);
    if (!row.category) errors.push(`${label}: "category" is required.`);
    if (!row.topic) errors.push(`${label}: "topic" is required.`);
    if (!row.subCategory1) {
      errors.push(`${label}: "subCategory1" is required (basics | intermediate | advanced).`);
    } else if (!VALID_SUB_CATEGORY1.includes(row.subCategory1)) {
      errors.push(
        `${label}: "subCategory1" must be basics, intermediate, or advanced (got "${row.subCategory1}").`
      );
    }
    if (!row.subCategory2) errors.push(`${label}: "subCategory2" is required.`);

    return row;
  });

  return { items, errors };
}

export function formatBulkQuestionsJson(items) {
  return JSON.stringify(items, null, 2);
}

export function aiFormDefaults(aiForm) {
  return {
    category: cleanString(aiForm?.category),
    topic: cleanString(aiForm?.topic),
    subCategory1: cleanString(aiForm?.subCategory1).toLowerCase(),
    subCategory2: cleanString(aiForm?.subCategory2) || 'general',
  };
}
